import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Dumbbell, Apple, LineChart } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/fitness/clients/${id}`);
      setClient(data);
    })();
  }, [id]);

  if (!client) {
    return (
      <div className="px-4 md:px-8 py-8 text-muted-foreground">Loading…</div>
    );
  }

  return (
    <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
      <Link to="/app/fitness/clients" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to clients
      </Link>

      <div className="flex items-start gap-6 mb-8">
        <div className="h-20 w-20 rounded-full overflow-hidden bg-muted shrink-0">
          {client.photo_url ? (
            <img src={client.photo_url} className="h-full w-full object-cover" alt="" />
          ) : (
            <div className="h-full w-full grid place-items-center text-muted-foreground">
              {(client.name || "?").slice(0, 1)}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-4xl leading-tight">{client.name}</h1>
          <div className="text-sm text-muted-foreground">{client.email}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {client.goal && (
              <span className="text-[10px] uppercase tracking-[0.18em] rounded-full border border-border px-2 py-0.5">{client.goal}</span>
            )}
            {client.membership && (
              <span className="text-[10px] uppercase tracking-[0.18em] rounded-full bg-primary/10 text-primary px-2 py-0.5">{client.membership}</span>
            )}
          </div>
        </div>
      </div>

      <Tabs defaultValue="workouts">
        <TabsList>
          <TabsTrigger value="workouts"><Dumbbell className="h-4 w-4 mr-2" /> Workouts</TabsTrigger>
          <TabsTrigger value="nutrition"><Apple className="h-4 w-4 mr-2" /> Nutrition</TabsTrigger>
          <TabsTrigger value="progress"><LineChart className="h-4 w-4 mr-2" /> Progress</TabsTrigger>
        </TabsList>
        <TabsContent value="workouts" className="mt-6">
          {(client.workouts || []).length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No workouts assigned yet. Use the AI generator to create one in seconds.
            </div>
          ) : (
            <div className="space-y-3">
              {client.workouts.map((w) => (
                <div key={w.id} className="rounded-xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between">
                    <div className="font-display text-xl">{w.title}</div>
                    <div className="text-xs text-muted-foreground">{w.duration_min} min · {w.difficulty}</div>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">{w.description}</div>
                  <ul className="mt-3 text-sm space-y-1">
                    {w.blocks?.map((b, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="text-muted-foreground w-6">{i + 1}.</span>
                        <span className="flex-1">{b.exercise_name}</span>
                        <span className="text-muted-foreground">{b.sets}×{b.reps}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="nutrition" className="mt-6 text-sm text-muted-foreground">
          Nutrition module — coming soon in v1.1 (AI meal planner already scaffolded on the backend).
        </TabsContent>
        <TabsContent value="progress" className="mt-6 text-sm text-muted-foreground">
          Progress tracking, measurements and photos land in v1.1.
        </TabsContent>
      </Tabs>
    </div>
  );
}
